import React from 'react';
import styled from 'styled-components';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  padding: 2rem 0 6rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const PageSubtitle = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
  margin-bottom: 3rem;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.375rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border);
`;

const SectionDesc = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-bottom: 1.5rem;
`;

/* Colors */
const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
`;

const ColorSwatch = styled.div`
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);
`;

const SwatchColor = styled.div<{ $bg: string }>`
  height: 64px;
  background: ${({ $bg }) => $bg};
`;

const SwatchInfo = styled.div`
  padding: 0.5rem 0.75rem;
  background: var(--bg-surface);
`;

const SwatchName = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const SwatchHex = styled.div`
  font-size: 0.6875rem;
  color: var(--text-muted);
  font-family: 'SF Mono', monospace;
`;

/* Typography */
const TypeSample = styled.div`
  margin-bottom: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);
`;

const TypeLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.25rem;
`;

/* Component rows */
const ComponentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1.5rem;
`;

/* Buttons */
const DemoBtn = styled.button<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: ${({ $variant }) =>
    $variant === 'secondary' ? '1px solid var(--border)'
    : $variant === 'ghost' ? 'none'
    : 'none'};
  background: ${({ $variant }) =>
    $variant === 'primary' ? 'var(--accent)'
    : $variant === 'secondary' ? 'var(--bg-elevated)'
    : $variant === 'warning' ? 'var(--warning)'
    : 'transparent'};
  color: ${({ $variant }) =>
    $variant === 'primary' || $variant === 'warning' ? 'white'
    : $variant === 'secondary' ? 'var(--text-primary)'
    : 'var(--text-secondary)'};

  &:hover {
    ${({ $variant }) =>
      $variant === 'primary' ? 'background: var(--accent-hover); box-shadow: 0 4px 12px rgba(45,154,143,0.3);'
      : $variant === 'secondary' ? 'background: var(--bg-inset);'
      : 'background: var(--bg-elevated); color: var(--text-primary);'}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DemoSmBtn = styled(DemoBtn)`
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
`;

const DemoLgBtn = styled(DemoBtn)`
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
`;

/* Badges */
const Badge = styled.span<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  background: ${({ $variant }) =>
    $variant === 'teal' ? 'var(--accent-subtle)'
    : $variant === 'orange' ? 'var(--warning-subtle)'
    : $variant === 'blue' ? 'var(--info-subtle)'
    : $variant === 'green' ? 'var(--success-subtle)'
    : $variant === 'red' ? 'var(--error-subtle)'
    : 'var(--bg-elevated)'};
  color: ${({ $variant }) =>
    $variant === 'teal' ? 'var(--accent)'
    : $variant === 'orange' ? 'var(--warning)'
    : $variant === 'blue' ? 'var(--info)'
    : $variant === 'green' ? 'var(--success)'
    : $variant === 'red' ? 'var(--error)'
    : 'var(--text-muted)'};
`;

/* Cards */
const Card = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;

  &:hover { box-shadow: var(--shadow-md); }
`;

const CardTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const CardDesc = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

/* Form inputs */
const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-width: 400px;
  margin-bottom: 1rem;
`;

const InputLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  transition: all 0.2s;
  outline: none;

  &::placeholder { color: var(--text-muted); }
  &:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring); }
`;

const StatusDot = styled.span<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ $status }) =>
    $status === 'active' ? 'var(--success)'
    : $status === 'error' ? 'var(--error)'
    : 'var(--text-muted)'};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

/* ---------- data ---------- */
const COLORS = [
  { name: 'Accent', hex: '#2d9a8f', var: 'var(--accent)' },
  { name: 'Accent Hover', hex: '#1a7a70', var: 'var(--accent-hover)' },
  { name: 'Accent Subtle', hex: '#e8f7f6', var: 'var(--accent-subtle)' },
  { name: 'Background', hex: '#f8fafc', var: 'var(--bg-primary)' },
  { name: 'Surface', hex: '#ffffff', var: 'var(--bg-surface)' },
  { name: 'Elevated', hex: '#f1f5f9', var: 'var(--bg-elevated)' },
  { name: 'Text Primary', hex: '#0f172a', var: 'var(--text-primary)' },
  { name: 'Text Secondary', hex: '#475569', var: 'var(--text-secondary)' },
  { name: 'Text Muted', hex: '#94a3b8', var: 'var(--text-muted)' },
  { name: 'Success', hex: '#22c55e', var: 'var(--success)' },
  { name: 'Warning', hex: '#f97316', var: 'var(--warning)' },
  { name: 'Error', hex: '#ef4444', var: 'var(--error)' },
  { name: 'Info', hex: '#3b82f6', var: 'var(--info)' },
  { name: 'Border', hex: '#e5e7eb', var: 'var(--border)' },
];

/* ---------- component ---------- */
const DesignSystem: React.FC = () => (
  <PageBody className="fade-in">
    <Container>
      <PageTitle>Design System</PageTitle>
      <PageSubtitle>
        Lumina component library, colour palette, and typography reference
      </PageSubtitle>

      {/* Colors */}
      <Section>
        <SectionTitle>Color Palette</SectionTitle>
        <SectionDesc>All colours are defined as CSS custom properties and switch automatically with the theme.</SectionDesc>
        <ColorGrid>
          {COLORS.map((c) => (
            <ColorSwatch key={c.name}>
              <SwatchColor $bg={c.var} />
              <SwatchInfo>
                <SwatchName>{c.name}</SwatchName>
                <SwatchHex>{c.hex}</SwatchHex>
              </SwatchInfo>
            </ColorSwatch>
          ))}
        </ColorGrid>
      </Section>

      {/* Typography */}
      <Section>
        <SectionTitle>Typography</SectionTitle>
        <SectionDesc>Inter typeface with optical sizing. All headings use -0.02em letter-spacing.</SectionDesc>
        <TypeSample>
          <TypeLabel>H1 &mdash; 2.25rem / 600</TypeLabel>
          <h1>The quick brown fox jumps over the lazy dog</h1>
        </TypeSample>
        <TypeSample>
          <TypeLabel>H2 &mdash; 1.75rem / 600</TypeLabel>
          <h2>The quick brown fox jumps over the lazy dog</h2>
        </TypeSample>
        <TypeSample>
          <TypeLabel>H3 &mdash; 1.25rem / 600</TypeLabel>
          <h3>The quick brown fox jumps over the lazy dog</h3>
        </TypeSample>
        <TypeSample>
          <TypeLabel>Body &mdash; 1rem / Regular</TypeLabel>
          <p>The quick brown fox jumps over the lazy dog. Lumina uses the Inter typeface with feature settings cv11 and ss01.</p>
        </TypeSample>
        <TypeSample>
          <TypeLabel>Small &mdash; 0.875rem / Regular</TypeLabel>
          <p style={{ fontSize: '0.875rem' }}>The quick brown fox jumps over the lazy dog.</p>
        </TypeSample>
        <TypeSample>
          <TypeLabel>Caption &mdash; 0.75rem / Regular</TypeLabel>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>The quick brown fox jumps over the lazy dog.</p>
        </TypeSample>
      </Section>

      {/* Buttons */}
      <Section>
        <SectionTitle>Buttons</SectionTitle>
        <SectionDesc>Consistent button styles across the platform.</SectionDesc>
        <ComponentRow>
          <DemoBtn $variant="primary">Primary</DemoBtn>
          <DemoBtn $variant="secondary">Secondary</DemoBtn>
          <DemoBtn $variant="ghost">Ghost</DemoBtn>
          <DemoBtn $variant="warning">Warning</DemoBtn>
          <DemoBtn disabled>Disabled</DemoBtn>
        </ComponentRow>
        <ComponentRow>
          <DemoSmBtn $variant="primary">Small Primary</DemoSmBtn>
          <DemoSmBtn $variant="secondary">Small Secondary</DemoSmBtn>
          <DemoSmBtn $variant="ghost">Small Ghost</DemoSmBtn>
        </ComponentRow>
        <ComponentRow>
          <DemoLgBtn $variant="primary">Large Primary</DemoLgBtn>
          <DemoLgBtn $variant="secondary">Large Secondary</DemoLgBtn>
        </ComponentRow>
      </Section>

      {/* Badges */}
      <Section>
        <SectionTitle>Badges</SectionTitle>
        <SectionDesc>Status indicators and category labels.</SectionDesc>
        <ComponentRow>
          <Badge $variant="teal">Teal / Accent</Badge>
          <Badge $variant="orange">Orange / Warning</Badge>
          <Badge $variant="blue">Blue / Info</Badge>
          <Badge $variant="green">Green / Success</Badge>
          <Badge $variant="red">Red / Error</Badge>
          <Badge>Neutral</Badge>
        </ComponentRow>
      </Section>

      {/* Status Dots */}
      <Section>
        <SectionTitle>Status Dots</SectionTitle>
        <ComponentRow>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <StatusDot $status="active" /> Active
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <StatusDot $status="draft" /> Draft
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <StatusDot $status="error" /> Error
          </span>
        </ComponentRow>
      </Section>

      {/* Cards */}
      <Section>
        <SectionTitle>Cards</SectionTitle>
        <SectionDesc>Surface containers with subtle hover effects.</SectionDesc>
        <CardGrid>
          <Card>
            <CardTitle>Default Card</CardTitle>
            <CardDesc>A standard card with surface background, border, and hover shadow.</CardDesc>
          </Card>
          <Card style={{ cursor: 'pointer', borderColor: 'var(--accent)' }}>
            <CardTitle>Interactive Card</CardTitle>
            <CardDesc>Cards can be interactive with accent border and lift on hover.</CardDesc>
          </Card>
          <Card style={{ borderStyle: 'dashed' }}>
            <CardTitle>Dashed Card</CardTitle>
            <CardDesc>Used for &ldquo;create new&rdquo; actions with a dashed border style.</CardDesc>
          </Card>
        </CardGrid>
      </Section>

      {/* Form Inputs */}
      <Section>
        <SectionTitle>Form Inputs</SectionTitle>
        <SectionDesc>Text inputs with focus rings matching the accent colour.</SectionDesc>
        <InputGroup>
          <InputLabel>Default Input</InputLabel>
          <Input type="text" placeholder="Enter some text..." />
        </InputGroup>
        <InputGroup>
          <InputLabel>Email Input</InputLabel>
          <Input type="email" placeholder="you@company.com" />
        </InputGroup>
      </Section>
    </Container>
  </PageBody>
);

export default DesignSystem;
